import React, { useState, useEffect, useCallback } from 'react';
import { X, Phone, Mail, MapPin, AlertCircle, UserCheck, Calendar } from 'lucide-react';
import { apiClient } from '../../lib/api';
// Option value is the 2-letter code (the backend's canonical form), label the full name.
import { US_STATE_OPTIONS } from '../../lib/usStates';
import { useAuthStore } from '../../store/useAuthStore';
import { formatDate as formatDateUtil, dobInputBounds } from '../../utils/dateUtils';

type AnimalSpeciesOpt = 'dog' | 'cat' | 'horse' | 'bird' | 'reptile' | 'exotic' | 'other';
type AnimalSexOpt = 'male' | 'female' | 'male_neutered' | 'female_spayed' | 'unknown';

const SPECIES_OPTIONS: { value: AnimalSpeciesOpt; label: string }[] = [
  { value: 'dog', label: 'Dog' }, { value: 'cat', label: 'Cat' }, { value: 'horse', label: 'Horse' },
  { value: 'bird', label: 'Bird' }, { value: 'reptile', label: 'Reptile' }, { value: 'exotic', label: 'Exotic' },
  { value: 'other', label: 'Other' },
];
const ANIMAL_SEX_OPTIONS: { value: AnimalSexOpt; label: string }[] = [
  { value: 'male', label: 'Male (intact)' }, { value: 'female', label: 'Female (intact)' },
  { value: 'male_neutered', label: 'Male (neutered)' }, { value: 'female_spayed', label: 'Female (spayed)' },
  { value: 'unknown', label: 'Unknown' },
];

interface AddPatientFormProps {
  onClose: () => void;
  onSuccess: (patient: any) => void;
}

interface PatientFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: string;
  phone: string;
  email: string;
  address: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  medicalHistory: string;
  allergies: string;
  notes: string;
  // Vet clinics: the pet being treated (owner is the fields above).
  animal: {
    name: string;
    species: AnimalSpeciesOpt | '';
    breed: string;
    sex: AnimalSexOpt | '';
    weightLbs: string;
    ageYears: string;
  };
}

interface PatientFormErrors {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  medicalHistory?: string;
  allergies?: string;
  notes?: string;
  animal?: {
    name?: string;
    species?: string;
    weightLbs?: string;
  };
}

interface PotentialMatch {
  _id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone: string;
  email?: string;
  address?: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  similarity: 'high' | 'medium' | 'low';
  matchScore: number;
  matchDetails: {
    nameMatch: boolean;
    dobMatch: boolean;
    phoneMatch: boolean;
    emailMatch: boolean;
    addressMatch: boolean;
  };
}

const initialFormData: PatientFormData = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  sex: '',
  phone: '',
  email: '',
  address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA'
  },
  medicalHistory: '',
  allergies: '',
  notes: '',
  animal: { name: '', species: '', breed: '', sex: '', weightLbs: '', ageYears: '' }
};

// Debounce hook for search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const AddPatientForm: React.FC<AddPatientFormProps> = ({ onClose, onSuccess }) => {
  // Vet clinics create animal patients (a human owner + a pet). The logged-in
  // clinic's isVet flag drives the whole form's shape.
  const isVet = useAuthStore(s => (s.user?.physicianData as any)?.isVet === true);
  const [formData, setFormData] = useState<PatientFormData>(initialFormData);
  const [errors, setErrors] = useState<PatientFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [potentialMatches, setPotentialMatches] = useState<PotentialMatch[]>([]);
  const [_isSearching, setIsSearching] = useState(false); // Tracks duplicate search state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Single debounced signal that covers every field the duplicate search
  // cares about. Previously there were two effects (name-debounced + phone/
  // email/DOB undebounced) that ran in parallel and spammed the backend on
  // every keystroke. Combining + debouncing to 2s keeps it to one call per
  // pause in typing.
  const searchQuery = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
  const searchSignal = [
    formData.firstName,
    formData.lastName,
    formData.phone,
    formData.email,
    formData.dateOfBirth
  ].join('|');
  const debouncedSearchSignal = useDebounce(searchSignal, 2000);

  // Search for potential duplicates
  const searchForDuplicates = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setPotentialMatches([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await apiClient.searchPatients(query, { limit: 10 });
      
      if (response.success && response.data) {
        const matches = response.data.map((patient: any) => {
          // Calculate similarity score based on multiple factors
          let score = 0;
          let maxScore = 0;
          
          // Name matching (40% of total score)
          const firstNameMatch = patient.firstName.toLowerCase() === formData.firstName.toLowerCase();
          const lastNameMatch = patient.lastName.toLowerCase() === formData.lastName.toLowerCase();
          const firstNameSimilar = patient.firstName.toLowerCase().includes(formData.firstName.toLowerCase()) || 
                                  formData.firstName.toLowerCase().includes(patient.firstName.toLowerCase());
          const lastNameSimilar = patient.lastName.toLowerCase().includes(formData.lastName.toLowerCase()) || 
                                 formData.lastName.toLowerCase().includes(patient.lastName.toLowerCase());
          
          if (firstNameMatch && lastNameMatch) {
            score += 40;
          } else if (firstNameMatch || lastNameMatch) {
            score += 25;
          } else if (firstNameSimilar && lastNameSimilar) {
            score += 15;
          } else if (firstNameSimilar || lastNameSimilar) {
            score += 8;
          }
          maxScore += 40;
          
          // Date of birth matching (25% of total score)
          const dobMatch = patient.dateOfBirth === formData.dateOfBirth;
          if (dobMatch && formData.dateOfBirth) {
            score += 25;
          }
          maxScore += 25;
          
          // Phone number matching (20% of total score)
          const phoneMatch = patient.phone === formData.phone;
          const phoneNormalized = (phone: string) => phone.replace(/\D/g, '');
          const phoneNormalizedMatch = phoneNormalized(patient.phone) === phoneNormalized(formData.phone);
          
          if (phoneMatch && formData.phone) {
            score += 20;
          } else if (phoneNormalizedMatch && formData.phone) {
            score += 15;
          }
          maxScore += 20;
          
          // Email matching (10% of total score)
          const emailMatch = patient.email && formData.email && 
                             patient.email.toLowerCase() === formData.email.toLowerCase();
          if (emailMatch) {
            score += 10;
          }
          maxScore += 10;
          
          // Address matching (5% of total score)
          let addressScore = 0;
          if (patient.address && formData.address) {
            if (patient.address.street.toLowerCase() === formData.address.street.toLowerCase()) {
              addressScore += 2;
            }
            if (patient.address.city.toLowerCase() === formData.address.city.toLowerCase()) {
              addressScore += 1;
            }
            if (patient.address.state.toLowerCase() === formData.address.state.toLowerCase()) {
              addressScore += 1;
            }
            if (patient.address.zipCode === formData.address.zipCode) {
              addressScore += 1;
            }
          }
          score += addressScore;
          maxScore += 5;
          
          // Calculate final percentage
          const finalScore = maxScore > 0 ? (score / maxScore) * 100 : 0;
          
          // Apply strong identifier penalties
          let similarity: 'high' | 'medium' | 'low' = 'low';
          
          // Strong identifier mismatch penalty
          const strongIdentifierMismatch = 
            (formData.phone && patient.phone && !phoneNormalizedMatch) ||
            (formData.email && patient.email && !emailMatch);
          
          if (finalScore >= 70) {
            similarity = 'high';
          } else if (finalScore >= 40) {
            similarity = 'medium';
          } else if (finalScore >= 20) {
            similarity = 'low';
          }
          
          // Apply strong identifier penalty
          if (strongIdentifierMismatch) {
            // If strong identifiers don't match, downgrade unless almost everything else matches
            if (similarity === 'high' && finalScore < 85) {
              similarity = 'medium';
            } else if (similarity === 'medium' && finalScore < 60) {
              similarity = 'low';
            }
          }
          
          // Special case: exact phone match is always high priority
          if (phoneMatch && formData.phone) {
            similarity = 'high';
          }
          
          // Special case: exact email match with similar name is high priority
          if (emailMatch && (firstNameMatch || lastNameMatch)) {
            similarity = 'high';
          }
          
          return {
            ...patient,
            similarity,
            matchScore: Math.round(finalScore),
            matchDetails: {
              nameMatch: firstNameMatch && lastNameMatch,
              dobMatch,
              phoneMatch: phoneMatch || phoneNormalizedMatch,
              emailMatch,
              addressMatch: addressScore > 2
            }
          };
        });

        // Filter and sort matches
        const filteredMatches = matches
          .filter((match: any) => 
            match.similarity === 'high' || 
            match.similarity === 'medium' ||
            (match.similarity === 'low' && match.matchScore >= 25)
          )
          .sort((a: PotentialMatch, b: PotentialMatch) => {
            // Sort by similarity first, then by match score
            const similarityOrder: Record<'high' | 'medium' | 'low', number> = { high: 3, medium: 2, low: 1 };
            if (similarityOrder[a.similarity] !== similarityOrder[b.similarity]) {
              return similarityOrder[b.similarity] - similarityOrder[a.similarity];
            }
            return b.matchScore - a.matchScore;
          })
          .slice(0, 5); // Limit to top 5 matches

        setPotentialMatches(filteredMatches);
        setShowDuplicateWarning(filteredMatches.length > 0);
      }
    } catch (error) {
      console.error('Error searching for duplicates:', error);
    } finally {
      setIsSearching(false);
    }
  }, [formData.firstName, formData.lastName, formData.dateOfBirth, formData.phone, formData.email, formData.address]);

  // Single effect driven by the combined debounced signal. searchForDuplicates
  // is intentionally NOT in deps — it rebuilds on every formData change and
  // would re-fire the search immediately, defeating the debounce.
  useEffect(() => {
    if (searchQuery.length >= 3) {
      searchForDuplicates(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchSignal]);

  // Clear duplicates when key fields change
  useEffect(() => {
    if (!formData.firstName && !formData.lastName) {
      setPotentialMatches([]);
      setShowDuplicateWarning(false);
    }
  }, [formData.firstName, formData.lastName]);

  const validateForm = (): boolean => {
    const newErrors: PatientFormErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    // DOB is required for human patients; for a vet order this is the owner's DOB
    // and is optional. Either way, reject a future date.
    if (!formData.dateOfBirth) {
      if (!isVet) newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      if (birthDate > today) {
        newErrors.dateOfBirth = 'Date of birth cannot be in the future';
      }
    }

    // Vet: require the pet's core identifiers.
    if (isVet) {
      const animalErr: NonNullable<PatientFormErrors['animal']> = {};
      if (!formData.animal.name.trim()) animalErr.name = 'Pet name is required';
      if (!formData.animal.species) animalErr.species = 'Species is required';
      const wt = parseFloat(formData.animal.weightLbs);
      if (!formData.animal.weightLbs || isNaN(wt) || wt <= 0) animalErr.weightLbs = 'Weight (lbs) is required';
      if (Object.keys(animalErr).length > 0) newErrors.animal = animalErr;
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?1?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Email validation (optional but must be valid if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Address validation
    if (!formData.address.street.trim()) {
      newErrors.address = { ...newErrors.address, street: 'Street address is required' };
    }
    if (!formData.address.city.trim()) {
      newErrors.address = { ...newErrors.address, city: 'City is required' };
    }
    if (!formData.address.state.trim()) {
      newErrors.address = { ...newErrors.address, state: 'State is required' };
    }
    if (!formData.address.zipCode.trim()) {
      newErrors.address = { ...newErrors.address, zipCode: 'ZIP code is required' };
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.address.zipCode)) {
      newErrors.address = { ...newErrors.address, zipCode: 'Please enter a valid ZIP code' };
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof PatientFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleAnimalChange = (field: keyof PatientFormData['animal'], value: string) => {
    setFormData(prev => ({ ...prev, animal: { ...prev.animal, [field]: value } }));
    if (errors.animal) setErrors(prev => ({ ...prev, animal: undefined }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value
      }
    }));

    // Clear address errors when user starts typing
    if (errors.address && errors.address[name as keyof typeof errors.address]) {
      setErrors(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [name]: undefined
        }
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    // Show warning if high similarity matches exist
    const highSimilarityMatches = potentialMatches.filter(match => match.similarity === 'high');
    if (highSimilarityMatches.length > 0 && !confirm(
      'We found similar patients in your records. Are you sure you want to create a new patient record?\n\n' +
      'Similar patients:\n' +
      highSimilarityMatches.map(match => `• ${match.firstName} ${match.lastName} (${match.phone})`).join('\n')
    )) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the data for API
      const { animal, ...rest } = formData;
      const patientData: any = {
        ...rest,
        // Parse date at noon UTC to avoid timezone day-shift issues. Owner DOB is
        // optional for vet orders, so only send it when provided.
        dateOfBirth: formData.dateOfBirth
          ? new Date(formData.dateOfBirth + 'T12:00:00.000Z').toISOString()
          : undefined,
        email: formData.email || undefined, // Send undefined if empty
        medicalHistory: formData.medicalHistory || undefined,
        allergies: formData.allergies || undefined,
        notes: formData.notes || undefined,
        ...(isVet ? {
          patientType: 'animal',
          animal: {
            name: animal.name.trim(),
            species: animal.species || undefined,
            breed: animal.breed.trim() || undefined,
            sex: animal.sex || undefined,
            weightLbs: animal.weightLbs ? parseFloat(animal.weightLbs) : undefined,
            ageYears: animal.ageYears ? parseFloat(animal.ageYears) : undefined,
          }
        } : {})
      };

      const response = await apiClient.createPatient(patientData);
      
      if (response.success) {
        onSuccess(response.data);
        onClose();
      } else {
        setSubmitError(response.error || 'Failed to create patient');
      }
    } catch (error: any) {
      setSubmitError(error.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse as UTC to avoid timezone shift for date-only fields like birthdays
    const date = new Date(dateString);
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return formatDateUtil(utcDate);
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 lg:px-6 py-4 lg:py-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">{isVet ? 'Add New Client & Pet' : 'Add New Patient'}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-6 lg:space-y-8">
            {/* Error Alert */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{submitError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Duplicate Warning */}
            {showDuplicateWarning && potentialMatches.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Potential Duplicate Patients Found
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p className="mb-2">We found similar patients in your records:</p>
                      <div className="space-y-2">
                        {potentialMatches.map((match) => (
                          <div
                            key={match._id}
                            className={`p-3 rounded-lg border-l-4 ${
                              match.similarity === 'high'
                                ? 'border-red-400 bg-red-50'
                                : match.similarity === 'medium'
                                ? 'border-yellow-400 bg-yellow-50'
                                : 'border-gray-400 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <UserCheck className="h-5 w-5 text-gray-500 mt-0.5" />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <p className="font-medium text-gray-900">
                                      {match.firstName} {match.lastName}
                                    </p>
                                    <span className="text-sm text-gray-500">
                                      ({match.matchScore}% match)
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-2 text-sm text-gray-600">
                                    <div className="flex items-center space-x-1">
                                      <Calendar className="h-3 w-3" />
                                      <span className={match.matchDetails?.dobMatch ? 'font-semibold text-red-600' : ''}>
                                        {formatDate(match.dateOfBirth)}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Phone className="h-3 w-3" />
                                      <span className={match.matchDetails?.phoneMatch ? 'font-semibold text-red-600' : ''}>
                                        {match.phone}
                                      </span>
                                    </div>
                                    {match.email && (
                                      <div className="flex items-center space-x-1">
                                        <Mail className="h-3 w-3" />
                                        <span className={match.matchDetails?.emailMatch ? 'font-semibold text-red-600' : ''}>
                                          {match.email}
                                        </span>
                                      </div>
                                    )}
                                    {match.address && (
                                      <div className="flex items-center space-x-1">
                                        <MapPin className="h-3 w-3" />
                                        <span className={match.matchDetails?.addressMatch ? 'font-semibold text-red-600' : ''}>
                                          {match.address.city}, {match.address.state}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {/* Match indicators */}
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {match.matchDetails?.nameMatch && (
                                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                        Name Match
                                      </span>
                                    )}
                                    {match.matchDetails?.dobMatch && (
                                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                        DOB Match
                                      </span>
                                    )}
                                    {match.matchDetails?.phoneMatch && (
                                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                        Phone Match
                                      </span>
                                    )}
                                    {match.matchDetails?.emailMatch && (
                                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                        Email Match
                                      </span>
                                    )}
                                    {match.matchDetails?.addressMatch && (
                                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                        Address Match
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  match.similarity === 'high'
                                    ? 'bg-red-100 text-red-800'
                                    : match.similarity === 'medium'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {match.similarity === 'high' ? 'High Risk' : 
                                   match.similarity === 'medium' ? 'Medium Risk' : 'Low Risk'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-xs">
                        Please review these existing patients before creating a new record.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Personal / Owner Information */}
            <div className="space-y-4 lg:space-y-6">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">{isVet ? 'Owner Information' : 'Personal Information'}</h3>
              {isVet && <p className="text-sm text-gray-500 -mt-2">The pet owner is the account holder and where the order ships.</p>}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="Enter first name"
                    required
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="Enter last name"
                    required
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isVet ? 'Owner Date of Birth (Optional)' : 'Date of Birth *'}
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    {...dobInputBounds()}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    required={!isVet}
                  />
                  {errors.dateOfBirth && (
                    <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
                  )}
                </div>

                {/* Optional in general, but required before a lab test can be
                    ordered: reference ranges are sex-specific. Hidden for vet
                    clinics, where these fields describe the human owner. */}
                {!isVet && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Biological Sex
                  </label>
                  <select
                    name="sex"
                    value={formData.sex}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                  >
                    <option value="">Not recorded</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Required to order lab tests — sets the reference ranges on the result.
                  </p>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="(555) 123-4567"
                    required
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="patient@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Pet Details (vet clinics only) */}
            {isVet && (
              <div className="space-y-4 lg:space-y-6">
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Pet Details</h3>
                <p className="text-sm text-gray-500 -mt-2">The animal this prescription is for. Weight is required for accurate dosing.</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pet Name *</label>
                    <input
                      type="text"
                      value={formData.animal.name}
                      onChange={(e) => handleAnimalChange('name', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                      placeholder="e.g., Rex"
                    />
                    {errors.animal?.name && <p className="mt-1 text-sm text-red-600">{errors.animal.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Species *</label>
                    <select
                      value={formData.animal.species}
                      onChange={(e) => handleAnimalChange('species', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm bg-white"
                    >
                      <option value="">Select species</option>
                      {SPECIES_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {errors.animal?.species && <p className="mt-1 text-sm text-red-600">{errors.animal.species}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Weight (lbs) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.animal.weightLbs}
                      onChange={(e) => handleAnimalChange('weightLbs', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                      placeholder="e.g., 68"
                    />
                    {errors.animal?.weightLbs && <p className="mt-1 text-sm text-red-600">{errors.animal.weightLbs}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Age (years)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.animal.ageYears}
                      onChange={(e) => handleAnimalChange('ageYears', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Breed</label>
                    <input
                      type="text"
                      value={formData.animal.breed}
                      onChange={(e) => handleAnimalChange('breed', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                      placeholder="e.g., Labrador"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sex</label>
                    <select
                      value={formData.animal.sex}
                      onChange={(e) => handleAnimalChange('sex', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm bg-white"
                    >
                      <option value="">Select</option>
                      {ANIMAL_SEX_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Address Information */}
            <div className="space-y-4 lg:space-y-6">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">{isVet ? 'Owner Address' : 'Address Information'}</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.address.street}
                    onChange={handleAddressChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="123 Main Street"
                    required
                  />
                                     {errors.address?.street && (
                     <p className="mt-1 text-sm text-red-600">{errors.address.street}</p>
                   )}
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    name="street2"
                    value={formData.address.street2}
                    onChange={handleAddressChange}
                    maxLength={35}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="Suite, Unit, Apt (optional, max 35 chars)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.address.city}
                    onChange={handleAddressChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="City"
                    required
                  />
                  {errors.address?.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <select
                    name="state"
                    value={formData.address.state}
                    onChange={handleAddressChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm bg-white"
                    required
                  >
                    <option value="">Select State</option>
                    {US_STATE_OPTIONS.map(state => (
                      <option key={state.code} value={state.code}>{state.name}</option>
                    ))}
                  </select>
                  {errors.address?.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.address.zipCode}
                    onChange={handleAddressChange}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                    placeholder="12345"
                    required
                  />
                  {errors.address?.zipCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.zipCode}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div className="space-y-4 lg:space-y-6">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">{isVet ? 'Additional Information' : 'Medical Information'}</h3>

              <div className="space-y-4 lg:space-y-6">
                {!isVet && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical History
                  </label>
                  <textarea
                    name="medicalHistory"
                    value={formData.medicalHistory}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm resize-y"
                    placeholder="Enter relevant medical history..."
                  />
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isVet ? 'Known Allergies / Sensitivities' : 'Known Allergies'}
                  </label>
                  <textarea
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm resize-y"
                    placeholder="Enter known allergies or type 'None'..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Physician Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 lg:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm resize-y"
                    placeholder="Additional notes about the patient..."
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col-reverse lg:flex-row lg:justify-end gap-3 lg:gap-4 pt-4 lg:pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full lg:w-auto px-6 py-3 lg:py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors text-base lg:text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full lg:w-auto px-6 py-3 lg:py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-medium transition-colors text-base lg:text-sm flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isVet ? 'Adding…' : 'Adding Patient...'}
                  </>
                ) : (
                  isVet ? 'Add Client & Pet' : 'Add Patient'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 